import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ToolButton } from 'components/common/ToolButton';
import { ToolIcon } from 'components/common/ToolIcon';
import { CopyIcon, CutIcon, PasteIcon, RedoIcon, UndoIcon } from 'components/common/icons';
import type { FormatMode } from 'state/FormatContext';
import type { ViewMode } from 'state/ViewModeContext';
import type { ValidationIssue } from 'model/validation';

interface OwnProps {
  isDirty: boolean;
  viewMode: ViewMode;
  formatMode: FormatMode;
  edgeType: string;
  showActionPalette: boolean;
  validationIssues: ValidationIssue[];
  onUndo: () => void;
  onRedo: () => void;
  onCut: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onToggleActionPalette: () => void;
  onAutoLayout: () => void;
  onToggleViewMode: () => void;
  onToggleFormatMode: () => void;
  onEdgeTypeChange: (next: string) => void;
  onReset: () => void;
  onDiscardLocalChanges: () => void;
  onSelectNode?: (nodeId: string) => void;
}

const StyledValidationWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const StyledValidationPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.dangerSurface};
  color: ${({ theme }) => theme.colors.dangerText};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    filter: brightness(0.97);
  }
`;

const StyledValidationDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  min-width: 320px;
  max-width: 480px;
  max-height: 320px;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.panel};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  border-radius: ${({ theme }) => theme.radii.sm};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  padding: 4px;
`;

const StyledValidationItem = styled.button<{ $clickable: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 6px 8px;
  font: inherit;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.radii.sm};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    background: ${({ theme, $clickable }) =>
      $clickable ? theme.colors.dangerSurface : 'transparent'};
  }
`;

const StyledValidationItemTarget = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dangerText};
  margin-right: 6px;
`;

const EditorToolbar = ({
  isDirty,
  viewMode,
  formatMode,
  edgeType,
  showActionPalette,
  validationIssues,
  onUndo,
  onRedo,
  onCut,
  onCopy,
  onPaste,
  onToggleActionPalette,
  onAutoLayout,
  onToggleViewMode,
  onToggleFormatMode,
  onEdgeTypeChange,
  onReset,
  onDiscardLocalChanges,
  onSelectNode,
}: OwnProps) => {
  const handleEdgeTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onEdgeTypeChange(e.target.value),
    [onEdgeTypeChange],
  );

  const [validationOpen, setValidationOpen] = useState(false);
  const validationWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!validationOpen) return undefined;
    const handlePointer = (e: MouseEvent) => {
      if (!validationWrapperRef.current?.contains(e.target as Node)) setValidationOpen(false);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setValidationOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [validationOpen]);

  useEffect(() => {
    if (validationIssues.length === 0) setValidationOpen(false);
  }, [validationIssues.length]);

  const togglePill = useCallback(() => setValidationOpen((v) => !v), []);
  const handleIssueClick = useCallback(
    (issue: ValidationIssue) => {
      if (issue.nodeId && onSelectNode) {
        onSelectNode(issue.nodeId);
        setValidationOpen(false);
      }
    },
    [onSelectNode],
  );

  return (
    <div className='editor-toolbar'>
      <div className='group' title='Edit history'>
        <ToolIcon onClick={onUndo} title='Undo (Ctrl+Z)'>
          <UndoIcon />
        </ToolIcon>
        <ToolIcon onClick={onRedo} title='Redo (Ctrl+Y)'>
          <RedoIcon />
        </ToolIcon>
      </div>
      <div className='divider' />
      <div className='group' title='Clipboard'>
        <ToolIcon onClick={onCut} title='Cut (Ctrl+X)'>
          <CutIcon />
        </ToolIcon>
        <ToolIcon onClick={onCopy} title='Copy (Ctrl+C)'>
          <CopyIcon />
        </ToolIcon>
        <ToolIcon onClick={onPaste} title='Paste (Ctrl+V)'>
          <PasteIcon />
        </ToolIcon>
      </div>
      <div className='divider' />
      <ToolButton
        onClick={onToggleActionPalette}
        active={showActionPalette}
        title={showActionPalette ? 'Hide action palette' : 'Show action palette'}
      >
        Actions
      </ToolButton>
      <ToolButton onClick={onAutoLayout} title='Re-arrange nodes to the auto layout'>
        Auto-layout
      </ToolButton>
      <ToolButton
        onClick={onToggleViewMode}
        active={viewMode === 'dataflow'}
        title={viewMode === 'flow' ? 'Switch to dataflow view' : 'Switch to flow view'}
      >
        {viewMode === 'flow' ? 'Flow' : 'Dataflow'}
      </ToolButton>
      <ToolButton
        onClick={onToggleFormatMode}
        active={formatMode === 'raw'}
        title={
          formatMode === 'formatted'
            ? 'Currently showing pretty labels — click to show raw identifiers'
            : 'Currently showing raw identifiers — click to show pretty labels'
        }
      >
        Names: {formatMode === 'formatted' ? 'Pretty' : 'Raw'}
      </ToolButton>
      {validationIssues.length > 0 && (
        <StyledValidationWrapper ref={validationWrapperRef}>
          <StyledValidationPill
            type='button'
            className='validation-pill'
            onClick={togglePill}
            aria-expanded={validationOpen}
            aria-haspopup='listbox'
            title='Show validation issues'
          >
            <span aria-hidden='true'>&#9888;</span>
            {validationIssues.length} validation issue{validationIssues.length === 1 ? '' : 's'}
          </StyledValidationPill>
          {validationOpen && (
            <StyledValidationDropdown role='listbox' aria-label='Validation issues'>
              {validationIssues.map((issue, idx) => {
                const clickable = Boolean(issue.nodeId && onSelectNode);
                return (
                  <StyledValidationItem
                    key={`${issue.nodeId ?? issue.edgeId ?? 'global'}-${idx}`}
                    type='button'
                    $clickable={clickable}
                    onClick={clickable ? () => handleIssueClick(issue) : undefined}
                    title={clickable ? `Select step ${issue.nodeId}` : undefined}
                  >
                    {issue.nodeId && (
                      <StyledValidationItemTarget>{issue.nodeId}</StyledValidationItemTarget>
                    )}
                    <span>{issue.message}</span>
                  </StyledValidationItem>
                );
              })}
            </StyledValidationDropdown>
          )}
        </StyledValidationWrapper>
      )}
      <select
        value={edgeType}
        onChange={handleEdgeTypeChange}
        title='Edge style'
        className='tool-select'
        aria-label='Edge style'
      >
        <option value='default'>Bezier</option>
        <option value='smoothstep'>Smooth Step</option>
        <option value='step'>Step</option>
        <option value='straight'>Straight</option>
      </select>

      <div className='toolbar-spacer' />

      {isDirty && (
        <>
          <ToolButton onClick={onReset} title='Reset to originally loaded workflow'>
            Reset
          </ToolButton>
          <ToolButton
            onClick={onDiscardLocalChanges}
            title='Clear local cache and discard unsaved changes'
          >
            Discard local
          </ToolButton>
        </>
      )}
    </div>
  );
};

export default React.memo(EditorToolbar);
